import React, { useMemo, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { useLanguage } from "../../i18n/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Shield,
  FileText,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  Siren,
  Info,
  ListChecks,
} from "lucide-react";
import { usePersonaColors } from "../../hooks/usePersonaColors";
import { perspectiveLabel, specialtyForMember } from "./perspectiveLabel";
import MarkdownOutline from "./MarkdownOutline";
import SectionActionBar from "./SectionActionBar";
import FeedbackRow from "./FeedbackRow";
import { stripEvidenceFromMarkdown } from "./evidenceUtils";

// Transparency sections rendered in fixed order. Each maps a parsed backend
// field to an anchor id, i18n title, and lucide icon.
const B2C_SECTIONS = [
  { field: "summary", id: "b2c-summary", titleKey: "query.b2cSummary", Icon: FileText, accent: "text-primary" },
  { field: "next_steps", id: "b2c-next-steps", titleKey: "query.b2cNextSteps", Icon: ListChecks, accent: "text-primary" },
  { field: "questions_for_doctor", id: "b2c-questions", titleKey: "query.b2cQuestions", Icon: HelpCircle, accent: "text-primary" },
  { field: "red_flags", id: "b2c-red-flags", titleKey: "query.b2cRedFlags", Icon: Siren, accent: "text-destructive" },
  { field: "consensus", id: "b2c-consensus", titleKey: "query.b2cConsensus", Icon: CheckCircle2, accent: "text-medical" },
  { field: "disagreements", id: "b2c-disagreements", titleKey: "query.b2cDisagreements", Icon: AlertTriangle, accent: "text-amber-600" },
];

const LEAD_FIELDS = new Set(["summary", "next_steps", "questions_for_doctor", "red_flags"]);

const ADVISOR_CHIP_CLASS =
  "text-xs font-medium text-medical border border-medical bg-card rounded-full px-2.5 py-1";

const ResultsDisplay = ({
  result,
  partialSynthesis,
  loading,
  queryId,
  proseClassName,
  onCopy,
  onDownload,
  onNewQuery,
  downloadingPdf,
  copiedId,
  proseScale,
  disableExport = false,
  disclaimerSentence = "",
  personas = [],
}) => {
  const { t } = useLanguage();
  const personaMap = usePersonaColors();
  const [activeOutlineId, setActiveOutlineId] = useState(null);

  const specialtyFor = (member) => specialtyForMember(member, personas, personaMap);

  const proseClasses =
    `prose ${proseClassName || "prose-base"} max-w-none prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-li:text-foreground/90 prose-headings:text-base clinical-prose-results`;

  // Present transparency sections (non-empty).
  const presentSections = useMemo(
    () => B2C_SECTIONS.filter((s) => result?.[s.field]?.trim()),
    [result]
  );

  const hasStructured = presentSections.length > 0;

  // Degraded fallback: parser could not split the response into sections.
  const degradedText =
    !hasStructured
      ? (result?.raw_output?.trim() || result?.synthesis?.trim() || "")
      : "";

  const individuals = result?.individual_responses || [];
  const selectedMembers = (result?.council_used?.length ? result.council_used : individuals);
  const advisorLabels = selectedMembers
    .map((member) => perspectiveLabel(specialtyFor(member), t))
    .filter(Boolean);
  const perspectiveLabels = individuals.map((resp) =>
    perspectiveLabel(specialtyFor(resp), t)
  );
  const summarySection = presentSections.find((section) => section.field === "summary");
  const leadSections = presentSections.filter(
    (section) => section.field !== "summary" && LEAD_FIELDS.has(section.field)
  );
  const laterSections = presentSections.filter((section) => !LEAD_FIELDS.has(section.field));

  // Footer sentence is the audited disclaimer when the client has loaded it.
  const disclaimerText =
    disclaimerSentence.trim() || result?.disclaimers?.trim() || t("query.b2cDisclaimer");

  // Assembled report text for the "copy report" action (mirrors on-screen content,
  // including the persistent disclaimer that always renders below).
  const reportCopyText = useMemo(() => {
    if (!result) return "";
    const disclaimerBlock = `## ${t("query.b2cDisclaimerTitle")}\n\n${disclaimerText}`;
    if (!hasStructured) {
      return degradedText
        ? `${degradedText}\n\n${disclaimerBlock}`
        : disclaimerBlock;
    }
    const parts = presentSections.map(
      (s) => `## ${t(s.titleKey)}\n\n${result[s.field].trim()}`
    );
    parts.push(disclaimerBlock);
    return parts.join("\n\n");
  }, [result, hasStructured, presentSections, degradedText, disclaimerText, t]);

  const outlineSections = useMemo(() => {
    if (!result) return [];
    const sections = [];

    if (summarySection) {
      sections.push({ id: summarySection.id, label: t(summarySection.titleKey), level: 1 });
    }
    leadSections.forEach((section) => {
      sections.push({ id: section.id, label: t(section.titleKey), level: 1 });
    });
    sections.push({ id: "perspective-row", label: t("query.perspectiveRow"), level: 1 });
    if (hasStructured) {
      laterSections.forEach((s) => {
        sections.push({ id: s.id, label: t(s.titleKey), level: 1 });
      });
    } else if (degradedText) {
      sections.push({ id: "b2c-full-response", label: t("query.b2cFullResponse"), level: 1 });
    }

    sections.push({ id: "b2c-disclaimer", label: t("query.b2cDisclaimerTitle"), level: 1 });

    if (result.pii_redacted) {
      sections.push({ id: "metadata", label: t("query.metadata"), level: 1 });
    }

    return sections;
  }, [result, t, hasStructured, summarySection, leadSections, laterSections, degradedText]);

  const scrollToSection = useCallback((id) => {
    setActiveOutlineId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const mainContent = result && (
    <div className="space-y-4 animate-fade-in min-w-0 flex-1">
      {/* Header + report actions */}
      <Card className="border border-border shadow-sm" data-testid="b2c-result-header">
        <CardHeader className="pb-0 px-4 md:px-5">
          <div className="flex items-center gap-2 pb-1">
            <FileText className="w-4 h-4 text-primary flex-shrink-0" />
            <CardTitle className="text-base font-semibold">{t("query.b2cResultTitle")}</CardTitle>
          </div>
          <div className={`${proseClasses} pb-3`} data-testid="second-opinion-intro">
            <p className="my-0 text-foreground/90">{t("query.b2cResultNote")}</p>
            {advisorLabels.length > 0 ? (
              <div className="not-prose mt-2 flex flex-wrap gap-2" data-testid="selected-advisors">
                {advisorLabels.map((label, index) => (
                  <span
                    key={`${label}-${index}`}
                    data-testid={`selected-advisor-chip-${index}`}
                    className={ADVISOR_CHIP_CLASS}
                  >
                    {label}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          {onCopy && onDownload && (
            <SectionActionBar
              variant="report"
              copyText={reportCopyText}
              onCopy={onCopy}
              onDownload={onDownload}
              onNewQuery={onNewQuery}
              showNewQuery={!!onNewQuery}
              disableExport={disableExport}
              downloadingPdf={downloadingPdf}
              copiedId={copiedId}
              proseScale={proseScale}
            />
          )}
        </CardHeader>
      </Card>

      {summarySection && (() => {
        const SummaryIcon = summarySection.Icon;
        return (
        <Card
          id={summarySection.id}
          className="border border-border scroll-mt-28"
          data-testid={`b2c-section-${summarySection.field}`}
        >
          <CardHeader className="pb-2 px-4 md:px-5">
            <div className="flex items-center gap-2">
              <SummaryIcon className={`w-4 h-4 flex-shrink-0 ${summarySection.accent}`} />
              <CardTitle className="text-base font-semibold">
                {t(summarySection.titleKey)}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 md:px-5 pb-4 pt-1">
            <div className={proseClasses}>
              <ReactMarkdown>{stripEvidenceFromMarkdown(result[summarySection.field])}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {leadSections.map(({ field, id, titleKey, Icon, accent }) => (
        <Card
          key={id}
          id={id}
          className={`border scroll-mt-28 ${field === "red_flags" ? "border-destructive/40" : "border-border"}`}
          data-testid={`b2c-section-${field}`}
        >
          <CardHeader className="pb-2 px-4 md:px-5">
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 flex-shrink-0 ${accent}`} />
              <CardTitle className="text-base font-semibold">
                {t(titleKey)}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 md:px-5 pb-4 pt-1">
            <div className={proseClasses}>
              <ReactMarkdown>{stripEvidenceFromMarkdown(result[field])}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      ))}

      <div
        id="perspective-row"
        className="rounded-md border border-medical bg-mint px-4 py-3 scroll-mt-28"
        data-testid="perspective-row"
      >
        <p className="text-xs font-semibold text-medical mb-2" data-testid="perspective-marker">
          {t("query.perspectiveMarker")}
        </p>
        <div className="flex flex-wrap gap-2">
          {perspectiveLabels.map((label, index) => (
            <span
              key={`${label}-${index}`}
              data-testid={`perspective-chip-${index}`}
              className={ADVISOR_CHIP_CLASS}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
      <div data-testid="human-review-slot" aria-hidden="true" />

      {laterSections.map(({ field, id, titleKey, Icon, accent }) => (
        <Card
          key={id}
          id={id}
          className={`border scroll-mt-28 ${field === "red_flags" ? "border-destructive/40" : "border-border"}`}
          data-testid={`b2c-section-${field}`}
        >
          <CardHeader className="pb-2 px-4 md:px-5">
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 flex-shrink-0 ${accent}`} />
              <CardTitle className={`text-base font-semibold ${field === "consensus" ? "text-medical" : ""}`}>
                {t(titleKey)}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 md:px-5 pb-4 pt-1">
            <div className={proseClasses}>
              <ReactMarkdown>{stripEvidenceFromMarkdown(result[field])}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Degraded fallback — parser could not split into sections */}
      {!hasStructured && degradedText && (
        <Card id="b2c-full-response" className="border border-border shadow-sm scroll-mt-28">
          <CardHeader className="pb-2 px-4 md:px-5">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <CardTitle className="text-base font-semibold">{t("query.b2cFullResponse")}</CardTitle>
            </div>
            <p className="text-xs text-muted-foreground pt-1">{t("query.b2cFullResponseNote")}</p>
          </CardHeader>
          <CardContent className="px-4 md:px-5 pb-4 pt-1">
            <div className={proseClasses}>
              <ReactMarkdown>{stripEvidenceFromMarkdown(degradedText)}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Persistent disclaimer */}
      <Card
        id="b2c-disclaimer"
        className="border border-primary/20 bg-primary/5 scroll-mt-28"
        data-testid="b2c-disclaimer"
      >
        <CardContent className="px-4 md:px-5 py-3">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground">{t("query.b2cDisclaimerTitle")}</p>
              <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{disclaimerText}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Metadata */}
      {result.pii_redacted && (
        <div
          id="metadata"
          className="flex items-center gap-3 text-xs text-muted-foreground bg-muted/50 rounded-sm p-3 scroll-mt-28"
        >
          <Badge variant="secondary" className="text-xs">
            <Shield className="w-3 h-3 mr-1" />
            {result.pii_entity_count} {t("query.piiRedacted")}
          </Badge>
        </div>
      )}

      <FeedbackRow queryId={queryId || result.query_id} />
    </div>
  );

  return (
    <>
      {/* Streaming synthesis preview */}
      {loading && partialSynthesis && (
        <Card className="border border-primary/20 overflow-hidden" data-testid="streaming-preview-card">
          <CardHeader className="pb-2 px-4 md:px-5">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("query.clinicalSummaryGenerating") || t("query.chairmanStreaming")}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 md:px-5 pb-4">
            <div className={`${proseClasses} text-base leading-relaxed`}>
              <ReactMarkdown>{partialSynthesis}</ReactMarkdown>
            </div>
            <span className="inline-block w-1.5 h-4 bg-primary/70 animate-pulse ml-0.5 align-text-bottom" />
          </CardContent>
        </Card>
      )}

      {result && (
        <div className="space-y-3">
          <MarkdownOutline
            sections={outlineSections}
            activeId={activeOutlineId}
            onSelect={scrollToSection}
            variant="chips"
          />

          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            <aside className="hidden lg:block w-60 shrink-0">
              <MarkdownOutline
                sections={outlineSections}
                activeId={activeOutlineId}
                onSelect={scrollToSection}
                variant="sidebar"
              />
            </aside>
            {mainContent}
          </div>
        </div>
      )}
    </>
  );
};

export default ResultsDisplay;
